import React, {useState} from "react";
import {useTheme} from "../../../../connectui/theme/ThemeProvider";
import {buildStyles} from "../../../../connectui/component/buildStyles";
import Text from "../../../../connectui/component/display/Text";
import FlexContainer from "../../../../connectui/component/layout/FlexContainer";
import Container from "../../../../connectui/component/layout/Container";
import ButtonText from "../../../../connectui/component/input/button/ButtonText";
import OutlinedButtonBase from "../../../../connectui/component/input/button/base/OutlinedButtonBase";
import CheckBox from "../../../../connectui/component/input/CheckBox";
import DropdownExpand from "../../../../connectui/component/display/dropdown/DropdownExpand";
import ListComponent from "../../../../connectui/component/layout/list/ListComponent";
import ListItemContainer from "../../../../connectui/component/layout/list/ListItemContainer";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {resolveBookingMergeConflicts} from "../../../../redux/actions/bookingsActions";
import {
  closeGeneralSuccessAlert,
  closeGeneralError,
  openGeneralSuccessAlert,
  openGeneralError,
} from "redux/actions/settingsActions";

const useStyles = (theme) =>
  buildStyles({
    mergingContactSection: {
      flex: 3,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.radius.lg,
      padding: theme.spacing(4),
    },
    confirmationButtonContainer: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
      display: "flex",
      gap: theme.spacing(2),
      "& > button": {
        flex: 1,
      },
    },
  });

const MergeGuestConfirmationPage = ({
  conflictsData,
  selectedDisplayValues,
  onBack,
  onMerge,
  primaryMergeCandidate,
  secondaryMergeCandidate,
}) => {
  const {t} = useTranslation();
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();

  // Move state to this component
  const [isPermanentChecked, setIsPermanentChecked] = useState(false);
  const [isMergeChecked, setIsMergeChecked] = useState(false);
  const [isListingSectionExpanded, setIsListingSectionExpanded] =
    useState(true);

  // Move handlers to this component
  const handlePermanentCheckChange = (newChecked) => {
    setIsPermanentChecked(newChecked);
  };

  const handleMergeCheckChange = (newChecked) => {
    setIsMergeChecked(newChecked);
  };

  const handleListingSectionExpandToggle = () => {
    setIsListingSectionExpanded((prev) => !prev);
  };

  const handleSuccessAlertClose = () => dispatch(closeGeneralSuccessAlert());
  const handleErrorAlertClose = () => dispatch(closeGeneralError());

  const handleConfirmMerge = () => {
    // Only proceed if both checkboxes are checked
    if (isPermanentChecked && isMergeChecked) {
      // Prepare the conflicts data with the actual values (not display values)
      const processConflicts = (type) => {
        return conflictsData[type].conflicts.map((conflict) => {
          const fieldKey = `${type}.${conflict.field}`;
          const isPrimary =
            selectedDisplayValues[fieldKey] === conflict.primary_value;
          return {
            field: conflict.field,
            value: selectedDisplayValues[fieldKey],
            selectedValue: isPrimary ? "primary" : "secondary",
          };
        });
      };

      const allConflicts = {
        guest: processConflicts("guest"),
        booking: processConflicts("booking"),
        listing: processConflicts("listing"),
      };

      // Populate updateDict with the actual values selected (not display values)
      const updateDict = {
        guest: {},
        booking: {},
        listing: {},
      };

      Object.entries(allConflicts).forEach(([type, conflicts]) => {
        conflicts.forEach((conflict) => {
          updateDict[type][conflict.field] = conflict.value;
        });
      });

      const payload = {
        primary_merge_candidate: primaryMergeCandidate,
        secondary_merge_candidate: secondaryMergeCandidate,
        update_dict: updateDict,
      };

      dispatch(
        resolveBookingMergeConflicts(
          payload,
          () => {
            onMerge && onMerge();
            dispatch(
              openGeneralSuccessAlert({
                open: true,
                message: "Guests merged successfully",
                onClose: handleSuccessAlertClose,
              }),
            );
            // Navigate to the primary candidate's page
            window.location.href = `/admin/messages/${primaryMergeCandidate.guest_id}`;
          },
          (error) => {
            console.error("Error resolving merge conflicts:", error);
            dispatch(
              openGeneralError({
                message: "Failed to merge guests",
                subtitle:
                  error?.response?.data?.error?.[0]?.message ||
                  "Please try again later.",
                open: true,
                disableSeverity: true,
                onClose: handleErrorAlertClose,
              }),
            );
          },
        ),
      );
    }
  };

  // Helper function to collect display values for a conflict type
  const getDisplayFieldsForType = (type) => {
    const conflicts = conflictsData[type].conflicts || [];
    const autoResolvedFields = conflictsData[type].auto_resolved_fields || [];

    // Collect manually resolved conflicts
    const manuallyResolvedFields = conflicts.reduce((acc, conflict) => {
      const fieldKey = `${type}.${conflict.field}`;
      return {
        ...acc,
        [conflict.field]: selectedDisplayValues[fieldKey],
      };
    }, {});

    // Collect auto-resolved fields
    const autoFields = autoResolvedFields.reduce(
      (acc, field) => ({
        ...acc,
        [field.field]: field.display_value,
      }),
      {},
    );

    return {...manuallyResolvedFields, ...autoFields};
  };

  // Get display fields for each type
  const guestDisplayFields = getDisplayFieldsForType("guest");
  const bookingDisplayFields = getDisplayFieldsForType("booking");
  const listingDisplayFields = getDisplayFieldsForType("listing");

  // Helper function to render a contact row
  const renderConfirmPageRow = (label, value, customStyle = {}) => {
    if (value === null) return null;

    return (
      <Container
        sx={{marginBottom: theme.spacing(4), "&:last-child": {marginBottom: 0}}}
      >
        <Text
          variant="textSM"
          sx={{
            color: theme.colors.textSecondary,
            marginBottom: theme.spacing(1),
          }}
        >
          {label}
        </Text>
        <Text
          variant="textMD"
          sx={{color: theme.colors.textPrimary, ...customStyle}}
        >
          {value}
        </Text>
      </Container>
    );
  };

  // Helper function to render a section with fields in two columns
  const renderSection = (title, fields, isCollapsible = false) => {
    // Filter out fields with no values
    const nonEmptyFields = Object.entries(fields).filter(
      ([key, value]) => value !== undefined && value !== null && value !== "",
    );

    if (nonEmptyFields.length === 0) {
      return null;
    }

    const content = (
      <FlexContainer flexWrap="wrap" gap={theme.spacing(4)}>
        {nonEmptyFields.map(([key, value], index) => (
          <Container key={key} sx={{flex: "0 0 calc(50% - 16px)"}}>
            {key === "status"
              ? renderConfirmPageRow(
                  key
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" "),
                  value,
                  {
                    backgroundColor: theme.colors.blue50,
                    color: theme.colors.blue700,
                    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
                    borderRadius: theme.radius.md,
                    display: "inline-block",
                  },
                )
              : renderConfirmPageRow(
                  key
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" "),
                  value,
                )}
          </Container>
        ))}
      </FlexContainer>
    );

    if (isCollapsible) {
      return (
        <DropdownExpand
          label={title}
          isExpanded={isListingSectionExpanded}
          onToggle={handleListingSectionExpandToggle}
          sx={{
            marginTop: theme.spacing(4),
            marginBottom: theme.spacing(4),
          }}
        >
          {content}
        </DropdownExpand>
      );
    }

    return (
      <>
        <Text
          variant="textLG"
          weight="semibold"
          sx={{marginTop: theme.spacing(4), marginBottom: theme.spacing(4)}}
        >
          {title}
        </Text>
        {content}
      </>
    );
  };

  return (
    <>
      <Container sx={{marginBottom: theme.spacing(4)}}>
        <Text variant="displayXS" weight="semibold">
          {t("merge-guest.review-confirm-merge")}
        </Text>
        <Text
          sx={{paddingTop: theme.spacing(2)}}
          variant="textSM"
          weight="medium"
          color="textSecondary"
        >
          {t("merge-guest.confirm-accuracy")}
        </Text>
      </Container>

      <FlexContainer flexDirection="row" gap="32px">
        <Container className={classes.mergingContactSection}>
          {renderSection(
            t("merge-guest.guest-information"),
            guestDisplayFields,
          )}
          {renderSection(
            t("merge-guest.booking-details"),
            bookingDisplayFields,
          )}
          {renderSection(
            t("merge-guest.listing-information"),
            listingDisplayFields,
            true,
          )}
        </Container>

        <Container sx={{flex: 2}}>
          <Text
            variant="textLG"
            weight="semibold"
            sx={{marginBottom: theme.spacing(4)}}
          >
            {t("merge-guest.summary-of-merged-data")}
          </Text>

          <ListComponent sx={{marginBottom: theme.spacing(6)}}>
            <ListItemContainer>
              <Text variant="textMD">
                {t("merge-guest.no-attributes-removed")}
              </Text>
            </ListItemContainer>
            <ListItemContainer>
              <Text variant="textMD">
                {t("merge-guest.all-bookings-saved")}
              </Text>
            </ListItemContainer>
            <ListItemContainer>
              <Text variant="textMD">
                {t("merge-guest.conflicts-resolved")}
              </Text>
            </ListItemContainer>
          </ListComponent>

          <Text
            variant="textMD"
            sx={{
              marginTop: theme.spacing(4),
              marginBottom: theme.spacing(1),
            }}
          >
            {t("merge-guest.contact-support")}
          </Text>
        </Container>
      </FlexContainer>

      <Container
        sx={{
          marginTop: "auto",
          paddingTop: theme.spacing(4),
          paddingBottom: theme.spacing(4),
        }}
      >
        <Container
          sx={{
            marginBottom: theme.spacing(2),
          }}
        >
          <FlexContainer alignItems="center" gap={theme.spacing(2)}>
            <CheckBox
              isChecked={isPermanentChecked}
              onChange={handlePermanentCheckChange}
              outline={true}
            />
            <Text variant="textMD">
              {t("merge-guest.permanent-merge-confirmation")}
            </Text>
          </FlexContainer>
        </Container>

        <Container
          sx={{
            marginBottom: theme.spacing(2),
          }}
        >
          <FlexContainer alignItems="center" gap={theme.spacing(2)}>
            <CheckBox
              isChecked={isMergeChecked}
              onChange={handleMergeCheckChange}
              outline={true}
            />
            <Text variant="textMD">
              {t("merge-guest.consolidate-confirmation")}
            </Text>
          </FlexContainer>
        </Container>

        <Container
          sx={{
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
            display: "flex",
            gap: theme.spacing(2),
            "& > button": {
              flex: 1,
            },
          }}
        >
          <ButtonText
            label={t("merge-guest.back")}
            buttonBase={OutlinedButtonBase}
            onClick={onBack}
            sx={{
              color: theme.colors.textSecondary,
              borderColor: theme.colors.border,
            }}
          />
          <ButtonText
            label={t("merge-guest.merge")}
            onClick={handleConfirmMerge}
            disabled={!isPermanentChecked || !isMergeChecked}
            color={theme.colors.white}
          />
        </Container>
      </Container>
    </>
  );
};

export default MergeGuestConfirmationPage;
